<template>
    <div>
        <!-- Default template always shown -->
        <div class="form-group" v-if="!backgroundCard">
            <div class="avatar-wrapper">
                <Avatar
                    :src="newImageBase64"
                    :showPreview="showPreview"
                    :isSquare="requirementsBelowImage"
                    :placeholder="placeholder"
                    :showDefaultPlaceholderImg="showDefaultPlaceholderImg"
                    @click="uploadPhoto"
                />

                <div class="avatar-spacer" v-if="showPreview"></div>

                <Requirements
                    v-if="showRequirements"
                    :requirements="requirementsList"
                    :isSizeOk="localIsSizeOk"
                    :isFormatOk="localIsFormatOk"
                    :horizontal="requirementsHorizontal"
                />

                <UploadButton
                    v-if="showButton"
                    :hasImage="!!newImageBase64"
                    :buttonType="buttonType"
                    @click.stop="uploadPhoto"
                />
            </div>
                <input type="file" class="hidden" ref="inputAvatarUpload" @change="onFileInputChange" />
        </div>

        <!-- Card scenario shown below if backgroundCard is true -->
        <div v-if="backgroundCard">
            <div class="d-flex add-image bg-medium pointer p-3 radius-top-left-normal radius-top-right-normal"
                :style="'background-image : url(' + newImageBase64 + ')'"
                @click.stop="uploadPhoto">
                
                <div class="d-flex flex-column align-items-start w-100">
                    <button
                        v-if="showButton"
                        class="btn secondary"
                        type="button"
                        @click.stop="uploadPhoto"
                    >
                        {{ newImageBase64 ? 'Edit Picture' : 'Select Picture' }}
                    </button>

                    <input type="file" class="hidden" ref="inputAvatarUpload" @change="onFileInputChange" />
                </div>
            </div>

            <div class="requirements-wrapper card-requirements mt-1 mb-1 d-flex justify-content-center">
                <Requirements
                    v-if="showRequirements"
                    :requirements="requirementsList"
                    :isSizeOk="localIsSizeOk"
                    :isFormatOk="localIsFormatOk"
                    :horizontal="requirementsHorizontal"
                />
            </div>
        </div>
    </div>
</template>

<script>
    async function bytesFromDataUrl(dataUrl) {
        const base64 = dataUrl.split(',')[1] || '';
        const binary = atob(base64);
        const len = binary.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
        return bytes.buffer;
    }

    async function computeSHA256(input) {
        try {
            let buffer;

            if (input instanceof ArrayBuffer) {
                buffer = input;
            } else if (input instanceof Blob || input instanceof File) {
                buffer = await input.arrayBuffer();
            } else if (typeof input === 'string' && input.startsWith('data:')) {
                buffer = await bytesFromDataUrl(input);
            } else {
                throw new TypeError('Expected ArrayBuffer/Blob/File or dataURL string');
            }

            const subtle = (typeof crypto !== 'undefined' && crypto.subtle)
                ? crypto.subtle
                : (typeof window !== 'undefined' && window.crypto && window.crypto.subtle ? window.crypto.subtle : null);
            
            if (!subtle) return null;

            const hashBuffer = await subtle.digest('SHA-256', buffer);
            const hashArray = Array.from(new Uint8Array(hashBuffer));

            return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        } catch (e) {
            return null;
        }
    }

    export default {
        components: {
            Avatar: {
                props: ['src', 'showPreview', 'isSquare', 'placeholder', 'showDefaultPlaceholderImg'],
                render(createElement) {
                    if (!this.showPreview) return null;
                    
                    let avatarClass = this.isSquare ? 'avatar-square' : 'avatar-circle';
                    let src, isSvg = false;

                    if (!this.src) {
                        if (this.showDefaultPlaceholderImg !== false) {
                            if (this.isSquare) {
                                src = `/static/images/auth/${this.placeholder}.jpg`;
                            } else {
                                isSvg = true;
                            }
                        } else {
                            // Render a transparent/empty placeholder
                            return createElement('div', {
                                class: ['avatar', avatarClass, 'avatar-placeholder', 'pointer'],
                                on: { click: this.$listeners.click }
                            });
                        }
                    } else {
                        src = this.src;
                    }

                    if (isSvg) {
                        return createElement('svg', {
                            attrs: {
                                xmlns: "http://www.w3.org/2000/svg",
                                viewBox: "0 0 512 512",
                                alt: "Click to add a picture"
                            },
                            class: ['avatar', avatarClass, 'pointer'],
                            on: { click: this.$listeners.click }
                        }, [
                            createElement('path', {
                                style: "fill:#8ca0b3;",
                                attrs: {
                                    d: "M8 256C8 119.033 119.033 8 256 8s248 111.033 248 248-111.033 248-248 248S8 392.967 8 256zm72.455 125.868C119.657 436.446 183.673 472 256 472s136.343-35.554 175.545-90.132c-3.141-26.99-22.667-49.648-49.538-56.366l-32.374-8.093C323.565 339.79 290.722 352 256 352s-67.565-12.21-93.634-34.591l-32.374 8.093c-26.87 6.718-46.396 29.376-49.537 56.366zM144 208c0 61.856 50.144 112 112 112s112-50.144 112-112S317.856 96 256 96s-112 50.144-112 112z"
                                }
                            })
                        ]);
                    }

                    return createElement('img', {
                        attrs: { src, alt: 'Click to add a picture', tabindex: 0 },
                        class: ['avatar', avatarClass, 'pointer'],
                        on: { click: this.$listeners.click }
                    });
                }
            },
            Requirements: {
                functional: true,
                props: ['requirements', 'isSizeOk', 'isFormatOk', 'horizontal'],
                render(createElement, context) {
                    function getColorClass(item, isSizeOk, isFormatOk) {
                        if (item.type === 'size' && !isSizeOk) return 'requirements-color color-6';
                        if (item.type === 'format' && !isFormatOk) return 'requirements-color color-6';
                        
                        return 'requirements-color';
                    }

                    if (context.props.horizontal) {
                        return createElement('div', { class: 'requirements-bar fs-small mb-4' },
                            context.props.requirements.map((item, idx) =>
                                createElement('span', { class: getColorClass(item, context.props.isSizeOk, context.props.isFormatOk) }, [
                                    item.text,
                                    idx < context.props.requirements.length - 1
                                        ? createElement('span', { style: 'margin: 0 0.5rem; color: #8ca0b3;' }, '|')
                                        : null
                                ])
                            )
                        );
                    }

                    // Default: vertical list
                    return createElement('ul', { class: ['fs-small', 'mb-4'] }, context.props.requirements.map((item) =>
                        createElement('li', { class: getColorClass(item, context.props.isSizeOk, context.props.isFormatOk) }, item.text)
                    ));
                }
            },
            UploadButton: {
                functional: true,
                props: ['hasImage', 'buttonType'],
                render(createElement, context) {
                    const label = context.props.hasImage
                        ? (context.props.buttonType === 'logo' ? 'Edit Logo' : 'Edit Picture')
                        : (context.props.buttonType === 'logo' ? 'Upload Logo' : 'Upload Picture');
                    
                    return createElement('button', {
                        class: ['btn', 'secondary', 'mt-2'],
                        on: { click: context.listeners.click }
                    }, label);
                }
            }
        },
        props: {
            value: {
                default: null
            },
            placeholder: {
                type: String,
                default: 'agent'
            },
            showPreview: {
                type: Boolean,
                default: true
            },
            requirementsBelowImage: {
                type: Boolean,
                default: false
            },
            showRequirements: {
                type: Boolean,
                default: true
            },
            showButton: {
                type: Boolean,
                default: true
            },
            buttonType: {
                type: String,
                default: 'picture'
            },
            isFormatOk: {
                type: Boolean,
                default: true
            },
            isSizeOk: {
                type: Boolean,
                default: true
            },
            showDefaultPlaceholderImg: {
                type: Boolean,
                default: true
            },
            requirementsHorizontal: {
                type: Boolean,
                default: false
            },
            backgroundCard: {
                type: Boolean,
                default: false
            },
            originalImage: {
                type: String,
                default: undefined
            },
            image: {
                type: String,
                default: undefined
            },
            imageBase64: {
                type: String,
                default: undefined
            },
            imageHash: {
                type: String,
                default: undefined
            },
            originalImageSync: {
                type: String,
                default: undefined
            },
            originalImageBase64Sync: {
                type: String,
                default: undefined
            },
            originalHashSync: {
                type: String,
                default: undefined
            }
        },
        data() {
            return {
                newImageBase64: this.value,
                requirementsList: [
                    { text: 'Max. file size: 1MB', type: 'size' },
                    { text: 'JPG, JPEG or PNG only', type: 'format' },
                    { text: 'At least 300x300 px recommended', type: 'info' }
                ],
                localIsFormatOk: this.isFormatOk,
                localIsSizeOk: this.isSizeOk,
                internalOriginalHash: undefined,
                baselineSet: false
            };
        },
        watch: {
            value(newVal) { 
                this.newImageBase64 = newVal; 
            },
            isFormatOk(newVal) { 
                this.localIsFormatOk = newVal; 
            },
            isSizeOk(newVal) { 
                this.localIsSizeOk = newVal; 
            }
        },
        mounted() {
            this.ensureInitialImageHash().then((hash) => {
                const img = this.newImageBase64 || null;
                const isDataUrl = typeof img === 'string' && img.startsWith('data:');

                this.$emit('update:imageBase64', isDataUrl ? img : null); // Only set base64 if it is base64
                this.$emit('update:image', img);                          // URL or dataURL goes here
                this.$emit('update:imageHash', hash || null);

                this.$emit('update:originalImageBase64Sync', isDataUrl ? img : null);
                this.$emit('update:originalImageSync', img);
                this.$emit('update:originalHashSync', hash || null);

                this.handleAvatarInput({
                phase: 'initial',
                image: img,
                uploadedHash: hash || null,
                isSizeOk: this.localIsSizeOk,
                isFormatOk: this.localIsFormatOk
                });
            });
        },
        methods: {
            uploadPhoto() { 
                this.$refs.inputAvatarUpload.click();
            },
            async onFileInputChange(event) {
                const file = event.target.files && event.target.files[0];
                
                if (!file) return;

                // Validation
                const name = (file.name || '').toLowerCase();
                const isJpg = name.endsWith('.jpg');
                const isJpeg = name.endsWith('.jpeg');
                const isPng = name.endsWith('.png');

                this.localIsSizeOk = this.validateSize(file.size);
                this.localIsFormatOk = (isJpg || isJpeg || isPng);

                // Keep parent in sync for Save validation
                this.$emit('update:isSizeOk', this.localIsSizeOk);
                this.$emit('update:isFormatOk', this.localIsFormatOk);

                // Existing immediate validation ping (parents no longer listen to this, but keep it for backward compat)
                this.$emit('input', {
                    image: null,
                    isSizeOk: this.localIsSizeOk,
                    isFormatOk: this.localIsFormatOk,
                    uploadedHash: null,
                    uploadedName: file.name || null,
                    phase: 'validation'
                });

                // Compute uploaded hash
                let uploadedHash = null;
                try { 
                    uploadedHash = await computeSHA256(file); 
                } catch (e) { 
                    uploadedHash = null; 
                }

                // Ensure baseline once (lazy)
                await this.ensureInitialImageHash();

                // Read dataURL and emit
                const reader = new window.FileReader();
                reader.onload = (e) => {
                    const fileBase64 = e.target.result;
                    this.newImageBase64 = fileBase64;

                    // Push "current" via .sync
                    this.$emit('update:imageBase64', fileBase64);
                    this.$emit('update:image', fileBase64);
                    this.$emit('update:imageHash', uploadedHash);

                    // Centralized decision + payload
                    this.handleAvatarInput({
                        phase: 'upload',
                        image: fileBase64,
                        isSizeOk: this.localIsSizeOk,
                        isFormatOk: this.localIsFormatOk,
                        uploadedHash,
                        uploadedName: file.name || null
                    });

                    this.$refs.inputAvatarUpload.value = null;
                };
                reader.readAsDataURL(file);
            },
            validateSize(givenSize) { 
                return (givenSize / (1024 * 1024)) <= 1; 
            },
            async ensureInitialImageHash() {
                if (this.baselineSet) {
                    return (this.originalHashSync != null) ? this.originalHashSync : this.internalOriginalHash;
                }

                const imgSource = (this.originalImage != null) ? this.originalImage : (this.value || this.newImageBase64 || null);
                let hash = (this.originalHashSync != null) ? this.originalHashSync : undefined;

                if (hash === undefined && imgSource) {
                    try {
                        if (typeof imgSource === 'string' && imgSource.startsWith('data:')) {
                            hash = await computeSHA256(imgSource);
                        } else if (typeof imgSource === 'string') {
                            const ab = await (async () => {
                                try {
                                    const resp = await fetch(imgSource, { credentials: 'same-origin' });

                                    if (!resp.ok) return null;

                                    return await resp.arrayBuffer();
                                } catch (e) { 
                                    return null; 
                                }
                            })();

                            hash = ab ? await computeSHA256(ab) : null;
                        }
                    } catch (e) { 
                        hash = null; 
                    }
                }

                this.internalOriginalHash = hash;
                this.baselineSet = true;

                return hash;
            },
            handleAvatarInput(payload) {
                const originalHash = (this.originalHashSync != null) ? this.originalHashSync : this.internalOriginalHash;
                let isDirty = false;
                let sameAsOriginal = false;

                if (payload.phase === 'initial') {
                    isDirty = false;
                    sameAsOriginal = true;
                } else if (payload.phase === 'upload') {
                    if (payload.uploadedHash && originalHash && payload.uploadedHash === originalHash) {
                        isDirty = false;
                        sameAsOriginal = true;

                        // Keep originals aligned when the user re-uploads the same image
                        this.$emit('update:originalImageBase64Sync', payload.image || null);
                        this.$emit('update:originalImageSync', payload.image || null);
                        this.$emit('update:originalHashSync', payload.uploadedHash || null);
                    } else {
                        isDirty = !!(payload.image && payload.uploadedHash && payload.isSizeOk && payload.isFormatOk);
                        sameAsOriginal = false;
                    }
                } else {
                    isDirty = false;
                    sameAsOriginal = false;
                }

                this.$emit('input', {
                    image: payload.image || null,
                    uploadedHash: payload.uploadedHash || null,
                    uploadedName: payload.uploadedName || null,
                    isSizeOk: payload.isSizeOk,
                    isFormatOk: payload.isFormatOk,
                    isDirty,
                    sameAsOriginal,
                    phase: payload.phase
                });

                // Keep flags synced as well
                this.$emit('update:isSizeOk', payload.isSizeOk);
                this.$emit('update:isFormatOk', payload.isFormatOk);
            }
        }
    }
</script>

<style lang="scss" scoped>
    .avatar {
        object-fit: cover;
        object-position: center center;
        display: block;
        margin: 0 auto;
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        cursor: pointer;
    }

    .avatar-circle {
        width: 6.25rem;
        max-width: 6.25rem;
        aspect-ratio: 1 / 1;
        border-radius: 50%;
    }

    .avatar-square {
        max-width: 12.5rem;
        width: 100%;
        aspect-ratio: 1 / 1;
        border-radius: 0;
    }

    .avatar-spacer {
        height: 1rem; 
        width: 100%;
        display: block;
    }

    .avatar-wrapper .avatar-circle ~ .fs-small,
    .avatar-wrapper .avatar-circle ~ ul.fs-small,
    .avatar-wrapper .avatar-circle ~ .btn,
    .avatar-wrapper .avatar-circle ~ .btn.secondary {
        text-align: center;
        margin-left: auto;
        margin-right: auto;
        display: block;
    }

    .requirements-color {
        color: #8ca0b3;
    }

    .add-image {
        height: 21rem;
        background-position: center;
        background-size: cover;
    }

    .requirements-wrapper .requirements-bar {
        text-align: left;
        width: 100%;
    }
</style>



// WEBPACK FOOTER //
// AvatarUploader.vue?995952e0